import { Link, Stack, Typography } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { AdminModeContext } from 'lib/context/app'
import { FC, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type SkipNavigationProps = {
    /** Whether to show the sidebar skip link */
    showSidebarLink?: boolean
    /** Whether to show the top bar (app-bar) skip link */
    showTopBarLink?: boolean
}

/**
 * Skip navigation component for accessibility (WCAG 2.4.1 Bypass Blocks)
 * Appears off-screen by default, becomes visible when focused
 */
export const SkipNavigation: FC<SkipNavigationProps> = ({ showSidebarLink = false, showTopBarLink = true }) => {
    const intl = useIntl()
    const adminModeContext = useContext(AdminModeContext)

    return (
        <Stack
            component="nav"
            aria-label={intl.formatMessage({ id: 'components_app_bar_skip_navigation', defaultMessage: 'Skip navigation' })}
            direction="column"
            spacing={2}
            sx={{
                position: 'fixed',
                top: '-200px',
                left: '8px',
                zIndex: 9999,
                p: 2,
                backgroundColor: 'background.default',
                boxShadow: 1,
                borderRadius: C1BorderRadius.large,
                transition: 'top 0.3s',
                '&:focus-within': {
                    top: '10px',
                },
            }}
        >
            <Typography>
                <FormattedMessage id="components_app_bar_skip_to" defaultMessage="Skip to:" />
            </Typography>
            {showTopBarLink && (
                <Link href="#app-bar" underline="always" color="primary">
                    <FormattedMessage id="components_app_bar_skip_to_top_bar" defaultMessage="Top bar" />
                </Link>
            )}
            {(showSidebarLink || (adminModeContext.isOnAdminPath && adminModeContext.hasAdminRole)) && (
                <Link href="#sidebar" underline="always" color="primary">
                    <FormattedMessage id="components_app_bar_skip_to_sidebar" defaultMessage="Sidebar" />
                </Link>
            )}
            <Link href="#main-content" underline="always" color="primary">
                <FormattedMessage id="components_app_bar_skip_to_main_content" defaultMessage="Main content" />
            </Link>
        </Stack>
    )
}
